import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom'
import { Provider } from 'react-redux'
import { Toaster } from 'react-hot-toast'
import './index.css'
import { store } from './store'
import { ThemeProvider } from './contexts/ThemeContext'
import { ModuleConfigProvider } from './contexts/ModuleConfigContext'
import { OwnerProvider } from './contexts/OwnerContext'
import ProtectedRoute from './components/ProtectedRoute'
import MainLayout from './layouts/MainLayout'
import DashboardPage from './pages/dashboard'
import CTEsPage from './pages/ctes'
import CTEsAnalysisPage from './pages/ctes/analysis'
import NFesPage from './pages/nfes'
import NFesAnalysisPage from './pages/nfes/analysis'
import DistributionCentersPage from './pages/distribution-centers'
import DistributionCentersAnalysisPage from './pages/distribution-centers/analysis'
import LogisticsPage from './pages/logistics'
import SalesForceAnalysisPage from './pages/logistics/sales-force-analysis'
import FreightAnalysisPage from './pages/logistics/freight-analysis'
import VehicleFreightAnalysisPage from './pages/logistics/vehicle-freight-analysis'
import SendDataForAnalysisPage from './pages/logistics/send-data-for-analysis'
import CostsPage from './pages/costs'
import CostsTrendsPage from './pages/costs/trends'
import ClientsPage from './pages/clients'
import ClientsOverviewPage from './pages/clients/overview'
import SendClientsPortfolioPage from './pages/clients/send-clients-portfolio'
import SearchResultsPage from './pages/search'
import SettingsPage from './pages/settings'
import { SpecializedQuestionsPage } from './pages/ai'

const App: React.FC = () => {
  return (
    <Provider store={store}>
      <OwnerProvider>
        <ThemeProvider>
          <ModuleConfigProvider>
            <Router>
            <Routes>
              <Route path="/" element={
                <ProtectedRoute>
                  <MainLayout />
                </ProtectedRoute>
              }>
                <Route index element={<DashboardPage />} />
                <Route path="dashboard" element={<DashboardPage />} />
                <Route path="ctes" element={<CTEsPage />} />
                <Route path="ctes/analysis" element={<CTEsAnalysisPage />} />
                <Route path="nfes" element={<NFesPage />} />
                <Route path="nfes/analysis" element={<NFesAnalysisPage />} />
                <Route path="distribution-centers" element={<DistributionCentersPage />} />
                <Route path="distribution-centers/analysis" element={<DistributionCentersAnalysisPage />} />
                <Route path="logistics" element={<LogisticsPage />} />
                <Route path="logistics/sales-force-analysis" element={<SalesForceAnalysisPage />} />
                <Route path="logistics/freight-analysis" element={<FreightAnalysisPage />} />
                <Route path="logistics/vehicle-freight-analysis" element={<VehicleFreightAnalysisPage />} />
                <Route path="logistics/send-data-for-analysis" element={<SendDataForAnalysisPage />} />
                <Route path="costs" element={<CostsPage />} />
                <Route path="costs/trends" element={<CostsTrendsPage />} />
                <Route path="clients" element={<ClientsPage />} />
                <Route path="clients/send-clients-portfolio" element={<SendClientsPortfolioPage />} />
                <Route path="clients/overview" element={<ClientsOverviewPage />} />
                <Route path="search" element={<SearchResultsPage />} />
                <Route path="settings" element={<SettingsPage />} />
                <Route path="ai/specialized-questions" element={<SpecializedQuestionsPage />} />
              </Route>
            </Routes>
            </Router>
            <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: 'var(--bg-card)',
                color: 'var(--text-primary)',
                border: '1px solid var(--border-primary)',
              },
              success: {
                duration: 3000,
                iconTheme: {
                  primary: '#22c55e',
                  secondary: '#fff',
                },
              },
              error: {
                duration: 5000,
                iconTheme: {
                  primary: '#ef4444',
                  secondary: '#fff',
                },
              },
            }}
            />
          </ModuleConfigProvider>
        </ThemeProvider>
      </OwnerProvider>
    </Provider>
  )
}

// Prevent multiple root creation
const rootElement = document.getElementById('root')
if (!rootElement) {
  throw new Error('Root element not found')
}

// Check if root already exists
let root = (window as any).__reactRoot
if (!root) {
  root = ReactDOM.createRoot(rootElement)
  ;(window as any).__reactRoot = root
}

root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
)
